import React, { useState } from "react";
import {
  Table,
  Tag,
  Button,
  Row,
  Select,
  Card,
  Typography,
  Statistic,
  Col,
  Tooltip,
  Tabs,
} from "antd";
import {
  DollarOutlined,
  ShoppingOutlined,
  ExclamationOutlined,
  TeamOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import type { ColumnsType } from "antd/es/table";
import { GET_INVOICES, GET_CLIENTS, GET_LOCATIONS, GET_ORDERS } from "./requests";
import LoadingSpinner from "../../components/loading";
import { typesUsers, invoiceStatus } from "../../utils/constants";
import moment from "moment-timezone";
import { Container } from "./elements";
import { useUser } from "../../providers/user";
import GeneralInvoiceModal from "./invoice-modal";
import { grey } from "@ant-design/colors";
import DetailDrawer from "./details";
import { order as orderType } from "../../types";
import InvoiceModal from "../History/modals/invoice-modal";

const { Text } = Typography;
const { TabPane } = Tabs;

const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 3px 6px rgba(0, 0, 0, 0.05)",
};

const Invoices: React.FC = () => {
  const { user } = useUser();
  const [showGeneralInvoiceModal, setShowGeneralInvoiceModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState();

  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 10,
  });
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [order, setOrder] = useState<typeof orderType.order | null>(null);

  // Si es manager, solo mostrar facturas de su location
  const locationFilter = user?.type === typesUsers.manager.value
    ? user?.location?.id
    : selectedLocation ? selectedLocation : undefined;

  const { loading, error, data, refetch: refetchInvoices } = useQuery(GET_INVOICES, {
    variables: {
      params: pageConfig,
      // status: { eq: invoiceStatus.valid.value },
      client: selectedClient ? selectedClient : undefined,
      location: locationFilter,
    },
  });

  const { loading: loadingOrders, data: ordersData, refetch: refetchOrders } = useQuery(GET_ORDERS, {
    variables: {
      params: pageConfig,
      client: selectedClient ? { eq: selectedClient } : undefined,
      location: locationFilter ? { eq: locationFilter } : undefined,
      requiredInvoice: true,
    },
  });

  const { data: clientsData } = useQuery(GET_CLIENTS, {
    variables: {
      params: { page: 1,
        pageSize: 5 },
      search: clientSearch,
    },
  });

  const { data: locationsData } = useQuery(GET_LOCATIONS, {
    variables: {
      params: { page: 1,
        pageSize: 5 },
      name: locationSearch,
    },
    skip: user?.type === typesUsers.manager.value,
  });

  const filteredOptions = clientsData?.clients?.results.map((client: any) => ({
    label: `${client.user?.firstName} ${client.user?.lastName}`,
    value: client.id,
  }));

  const filteredLocationsOptions = locationsData?.locations?.results.map((location: any) => ({
    label: `${location.name}`,
    value: location.id,
  }));

  if (error) return <div>Error: {error.message}</div>;

  const columnsEmitidas: ColumnsType<any> = [
    {
      title: "Cliente",
      key: "client",
      render: (record) => (
        <div>
          <Text style={{ fontWeight: "700" }}>
            {record.client?.user?.firstName} {record?.client?.user.lastName}
          </Text>
          <br />
          <Text style={{ color: "grey" }}>{record?.client?.fiscalData?.rfc}</Text>
        </div>
      ),
    },
    {
      title: "Estatus",
      key: "status",
      render: (record) => (
        <>
          <Tag color={invoiceStatus[record.status].color}>
            {invoiceStatus[record.status].name}
          </Tag>
          <br />
          <Text style={{ color: "grey" }}>
            {moment(record.createdAt).format("lll")}
          </Text>
        </>
      ),
    },
    {
      title: "Sucursal",
      key: "location",
      render: (record) => (
        <Text style={{ color: "green",
          fontSize: 16,
          fontWeight: "700" }}>
          {record.location?.name || "N/A"}
        </Text>
      ),
    },
    {
      title: "Cant. Ordenes",
      key: "orderTotal",
      render: (record) => (
        <>
          <Tag color={grey[0]}>
            {record?.orderTotal || 100}
          </Tag>
        </>
      ),
    },
    {
      title: "Documentos",
      key: "documents",
      render: (record) => (
        <div>
          {record.pdf && (
            <Button
              type="link"
              icon={<FilePdfOutlined />}
              onClick={() => window.open(record.pdf, '_blank')}
            >
              PDF
            </Button>
          )}
          {record.xml && (
            <Button
              type="link"
              icon={<FileTextOutlined />}
              onClick={() => window.open(record.xml, '_blank')}
            >
              XML
            </Button>
          )}
          {record.reference && (
            <Text style={{ color: "grey" }}>{'Folio: ' + record.reference}</Text>
          )}
        </div>
      ),
    },
    {
      // title: "Detalles",
      width: 200,
      key: "action",
      render: (row) => (
        <Row>
          <Tooltip placement="top" title="Ejemplos">
            <Button
              type="primary"
              size="large"
              style={{ margin: 5 }}
              onClick={() => {
                setSelectedInvoice(row?.id);
                setShowDetail(true);
              }}
            >
            Ver Detalles
            </Button>
          </Tooltip>
        </Row>
      ),
    },
  ];

  const columnsPendientes: ColumnsType<any> = [
    {
      title: "Cliente",
      dataIndex: "client",
      key: "client",
      render: (client) => (
        <div>
          <Text style={{ fontWeight: "700" }}>
            {client?.user?.firstName} {client?.user?.lastName}
          </Text>
        </div>
      ),
    },
    {
      title: "Sucursal",
      key: "location",
      render: (record) => (
        <Text style={{ color: "green",
          fontSize: 16,
          fontWeight: "700" }}>
          {record.location?.name || "N/A"}
        </Text>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (total) => (
        <Text style={{ fontWeight: "700" }}>
          {total}
        </Text>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (row) => (
        <>
          <Tooltip title={!row.requiredInvoice ? null : "Factura"}>
            <Button
              type="primary"
              style={{ marginLeft:5 }}
              icon={<FileTextOutlined />}
              onClick={()=>{
                setOrder(row)
                setShowInvoiceModal(true);
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const refetch = async () => {
    await refetchOrders();
    await refetchInvoices();
  }

  return (
    <Container style={{ padding: "20px",
      overflow: "scroll",
      height: "90vh" }}>
      {user?.type !== typesUsers.manager.value && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title="Facturas Totales"
                value={data?.invoices?.info?.count || 0}
                prefix={<ShoppingOutlined style={{ color: "#e67e22" }} />}
                suffix="🔥"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title="Clientes"
                value={0}
                prefix={<ExclamationOutlined style={{ color: "#f1c40f" }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title="Productos"
                value={0}
                prefix={<TeamOutlined style={{ color: "#3498db" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title="Ingresos"
                value={data?.invoices?.results?.reduce((acc: number, invoice: any) => acc + (invoice.order?.total || 0), 0) || 0}
                precision={2}
                prefix={<DollarOutlined style={{ color: "#2ecc71" }} />}
              />
            </Card>
          </Col>
        </Row>
      )}
      <Card
        style={{
          width: "100%",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Row style={{ padding: 20,
          alignItems: "end" }}>
          <div style={{ marginLeft: 10 }}>
            <Text style={{ fontWeight: "700",
              fontSize: 18 }}>
              Filtrar Cliente
            </Text>
            <br />
            <Select
              showSearch
              style={{ width: "200px" }}
              placeholder="Cliente"
              onSearch={(value) => setClientSearch(value)}
              onChange={(value) => setClientSearch(value)}
              onSelect={(value) => setSelectedClient(value)}
              onClear={() => setSelectedClient(null)}
              allowClear
              options={filteredOptions}
              filterOption={false}
            />
          </div>
          {user?.type !== typesUsers.manager.value && (
            <div style={{ marginLeft: 10 }}>
              <Text style={{ fontWeight: "700",
                fontSize: 18 }}>
          Filtrar Franquicia
              </Text>
              <br />
              <Select
                showSearch
                allowClear
                style={{ width: "200px" }}
                placeholder="Franquicia"
                onSearch={(value) => setLocationSearch(value)}
                onSelect={(value) => setSelectedLocation(value)}
                onClear={() => { setSelectedLocation(null); setLocationSearch(""); }}
                value={selectedLocation}
                options={filteredLocationsOptions}
                filterOption={false}
                disabled={user?.type === typesUsers.manager.value}
              />
            </div>
          )}
          <div style={{ marginLeft: "auto" }}>
            <Button type="primary" onClick={() => setShowGeneralInvoiceModal(true)}>Factura General</Button>
          </div>
        </Row>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Tabs defaultActiveKey="1" style={{ padding: "0 20px 20px" }}>
            <TabPane tab="Emitidas" key="1">
              <Table
                dataSource={data?.invoices?.results || []}
                columns={columnsEmitidas}
                rowKey="id"
                pagination={{
                  current: Number(pageConfig.page),
                  defaultCurrent: pageConfig.page,
                  pageSize: Number(pageConfig.pageSize),
                  defaultPageSize: pageConfig.pageSize,
                  total: data?.invoices?.info?.count,
                  showTotal: (total) => (
                    <Text disabled>{`${total} resultados`}</Text>
                  ),
                  onChange: (page, pageSize) => {
                    setPageConfig({
                      page,
                      pageSize,
                    });
                  },
                  showSizeChanger: true,
                }}
              />
            </TabPane>

            <TabPane tab="Pendientes" key="2">
              <Table
                loading={loadingOrders}
                dataSource={ordersData?.orders?.results.filter((order) => !order.invoice) || []}
                columns={columnsPendientes}
                rowKey="id"
                pagination={{
                  current: Number(pageConfig.page),
                  defaultCurrent: pageConfig.page,
                  pageSize: Number(pageConfig.pageSize),
                  defaultPageSize: pageConfig.pageSize,
                  total: data?.invoices?.info?.count,
                  showTotal: (total) => (
                    <Text disabled>{`${total} resultados`}</Text>
                  ),
                  onChange: (page, pageSize) => {
                    setPageConfig({
                      page,
                      pageSize,
                    });
                  },
                  showSizeChanger: true,
                }}
              />
            </TabPane>
          </Tabs>
        )}
      </Card>

      <GeneralInvoiceModal
        visible={showGeneralInvoiceModal}
        onClose={() => setShowGeneralInvoiceModal(false)}
        user={user}
      />
      <DetailDrawer
        setShowDetail={setShowDetail}
        showDetail={showDetail}
        selectedInoviceId={selectedInvoice}
      />
      <InvoiceModal
        showModal={showInvoiceModal}
        setShowModal={setShowInvoiceModal}
        refetch={refetch}
        order={order}
      />
    </Container>
  );
};

export default Invoices;
